/* Feb-2026 · Vendor Invoices — CFO Panel folder + thumbnail viewer.
 *
 * Grid of PDF/JPG/PNG thumbnails with vendor/amount/date labels,
 * an upload wizard (multipart → Object Storage), a preview modal
 * (inline `<img>` for images, `<embed>` for PDFs), inline metadata
 * editing (vendor, amount, invoice-date, category, notes, link to a
 * payable, paid toggle), and soft-delete.
 *
 * Auth comes from the admin session cookie via `adminApi`.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  FolderOpen, Upload, RefreshCw, Loader2, X, FileText, Image as ImageIcon,
  Trash2, Save, DollarSign, Calendar, Tag, Link2, CheckCircle2, Circle,
  Download, Search,
} from 'lucide-react';
import { adminApi } from '@/lib/api';
import { openConfirm } from '@/components/admin/ConfirmDialog';
import { cn } from '@/lib/utils';

const CATEGORIES = [
  'general', 'hosting', 'software', 'marketing', 'contractor',
  'office', 'travel', 'legal', 'design', 'utilities', 'freight', 'other',
];

const fmtUsd = (v) => {
  const n = Number(v || 0);
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

export default function VendorInvoicesSection() {
  const [rows, setRows]     = useState(null);
  const [busy, setBusy]     = useState(false);
  const [filter, setFilter] = useState({ paid: '', vendor: '', category: '' });
  const [summary, setSummary] = useState({ open: 0, paid: 0, count: 0 });
  const [previewId, setPreviewId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [payables, setPayables] = useState([]);
  const [hoverId, setHoverId] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (filter.paid)     params.paid = filter.paid;
      if (filter.vendor)   params.vendor = filter.vendor;
      if (filter.category) params.category = filter.category;
      const d = await adminApi.vendorInvoicesList(params);
      setRows(d.invoices || []);
      setSummary({ open: d.open_total_usd || 0, paid: d.paid_total_usd || 0,
                     count: d.count || 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load invoices.');
      setRows([]);
    } finally { setBusy(false); }
  }, [filter]);

  const loadPayables = useCallback(async () => {
    try {
      const d = await adminApi.listPayablesOpen();
      setPayables(d.payables || []);
    } catch { /* silently skip — payables link is optional */ }
  }, []);

  useEffect(() => { load(); loadPayables(); }, [load, loadPayables]);

  const preview = rows?.find((r) => r.id === previewId) || null;

  return (
    <div className="mb-5 rounded-md border border-border bg-card p-4"
         data-testid="acc-vendor-invoices">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[12.5px] font-semibold">
            <FolderOpen className="h-4 w-4" /> Vendor Invoices
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Upload vendor invoice files (PDF, JPG, PNG). Click any thumbnail to
            preview. Link each invoice to a payable to keep bookkeeping reconciled.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right text-[10.5px]">
            <div className="text-muted-foreground">Open&nbsp;<span className="font-mono-stat text-destructive">{fmtUsd(summary.open)}</span></div>
            <div className="text-muted-foreground">Paid&nbsp;<span className="font-mono-stat text-success">{fmtUsd(summary.paid)}</span></div>
          </div>
          <button type="button" onClick={load} disabled={busy}
                  data-testid="vinv-refresh"
                  className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2.5 py-1.5 text-[11px] hover:bg-secondary disabled:opacity-60">
            <RefreshCw className={cn('h-3 w-3', busy && 'animate-spin')} /> Refresh
          </button>
          <button type="button" onClick={() => setUploadOpen(true)}
                  data-testid="vinv-upload-open"
                  className="inline-flex items-center gap-1 rounded-sm bg-primary px-3 py-1.5 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90">
            <Upload className="h-3 w-3" /> Upload invoice
          </button>
        </div>
      </div>

      {/* Filter row */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-md border border-border bg-panel-header/40 p-0.5 text-[11px]"
             data-testid="vinv-paid-filter">
          {[['', 'All'], ['false', 'Open'], ['true', 'Paid']].map(([v, label]) => (
            <button key={label} onClick={() => setFilter((f) => ({ ...f, paid: v }))}
              data-testid={`vinv-paid-${v || 'all'}`}
              className={cn('rounded-sm px-2 py-0.5',
                filter.paid === v ? 'bg-primary text-primary-foreground'
                                   : 'text-muted-foreground hover:bg-secondary')}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1">
          <Search className="h-3 w-3 text-muted-foreground" />
          <input type="text" placeholder="Vendor…" value={filter.vendor}
                 onChange={(e) => setFilter((f) => ({ ...f, vendor: e.target.value }))}
                 data-testid="vinv-vendor-search"
                 className="w-32 bg-transparent text-[11px] outline-none" />
        </div>
        <select value={filter.category}
                onChange={(e) => setFilter((f) => ({ ...f, category: e.target.value }))}
                data-testid="vinv-category-filter"
                className="h-7 rounded-sm border border-border bg-background px-2 text-[11px]">
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button type="button" onClick={load}
                data-testid="vinv-apply-filters"
                className="rounded-sm border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary">
          Apply
        </button>
      </div>

      {/* Grid */}
      {rows === null ? (
        <div className="flex items-center gap-2 py-6 text-[11.5px] text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading invoices…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-muted/30 p-6 text-center text-[12px] text-muted-foreground"
             data-testid="vinv-empty">
          No invoices match your filters. Click <strong>Upload invoice</strong> to add one.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
             data-testid="vinv-grid">
          {rows.map((r) => (
            <InvoiceCard key={r.id} row={r}
              onOpen={() => setPreviewId(r.id)}
              onHover={(x, y) => { setHoverId(r.id); setHoverPos({ x, y }); }}
              onHoverEnd={() => setHoverId(null)}
              onTogglePaid={async () => {
                try {
                  const d = await adminApi.vendorInvoiceMarkPaid(r.id);
                  toast.success(d.invoice.paid ? 'Marked paid. Journal entry posted.'
                                                : 'Marked unpaid. Reversing entry posted.');
                  load();
                } catch (e) { toast.error(e?.response?.data?.detail || 'Toggle failed.'); }
              }}
            />
          ))}
        </div>
      )}

      {/* Hover zoom peek — cheap thumbnail magnifier without an extra click */}
      {hoverId && (() => {
        const r = rows.find((x) => x.id === hoverId);
        if (!r) return null;
        const isImage = (r.content_type || '').startsWith('image/');
        if (!isImage) return null;
        const url = adminApi.vendorInvoiceFileUrl(r.id);
        return (
          <div style={{ position: 'fixed', left: hoverPos.x + 12, top: hoverPos.y + 12,
                            width: 380, maxHeight: 500, zIndex: 80, pointerEvents: 'none',
                            border: '1px solid rgba(0,0,0,0.15)', borderRadius: 6,
                            background: '#fff', boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                            padding: 6 }}
                data-testid="vinv-hover-zoom">
            <img src={url} alt={r.filename}
                 style={{ maxWidth: '100%', maxHeight: 460, objectFit: 'contain',
                            display: 'block', margin: '0 auto' }} />
            <div style={{ fontSize: 11, textAlign: 'center', marginTop: 4, color: '#555' }}>
              {r.vendor} · {fmtUsd(r.amount_usd)} · {r.invoice_date}
            </div>
          </div>
        );
      })()}

      {uploadOpen && (
        <UploadModal onClose={() => setUploadOpen(false)}
                     onUploaded={() => { setUploadOpen(false); load(); }}
                     payables={payables} />
      )}
      {preview && (
        <PreviewModal invoice={preview} payables={payables}
                      onClose={() => setPreviewId(null)}
                      onChanged={load} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────── Card ───────────────

function InvoiceCard({ row, onOpen, onTogglePaid, onHover, onHoverEnd }) {
  const isImage = (row.content_type || '').startsWith('image/');
  const thumbUrl = adminApi.vendorInvoiceFileUrl(row.id);
  return (
    <div className="group flex flex-col overflow-hidden rounded-md border border-border bg-background transition hover:border-primary"
         data-testid={`vinv-card-${row.id}`}
         onMouseEnter={(e) => onHover?.(e.clientX, e.clientY)}
         onMouseMove={(e) => onHover?.(e.clientX, e.clientY)}
         onMouseLeave={() => onHoverEnd?.()}>
      <button type="button" onClick={onOpen}
              data-testid={`vinv-open-${row.id}`}
              title="Click to preview at full size"
              className="relative flex h-32 items-center justify-center overflow-hidden bg-muted/50">
        {isImage ? (
          <img src={thumbUrl} alt={row.filename}
               className="h-full w-full object-cover"
               loading="lazy" />
        ) : (
          <div className="flex flex-col items-center gap-1 text-muted-foreground">
            <FileText className="h-8 w-8" />
            <span className="text-[10px] uppercase tracking-wider">
              {(row.filename || '').split('.').pop()?.toUpperCase() || 'FILE'}
            </span>
          </div>
        )}
        {row.paid && (
          <div className="absolute right-1 top-1 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[9px] font-bold text-white">
            PAID
          </div>
        )}
      </button>
      <div className="p-2">
        <div className="line-clamp-1 text-[12px] font-semibold" title={row.vendor}>
          {row.vendor}
        </div>
        {row.requested_by && (
          <div className="line-clamp-1 text-[10px] text-muted-foreground" title={`Requested by ${row.requested_by}`}>
            Req: {row.requested_by}
          </div>
        )}
        <div className="flex items-center justify-between text-[10.5px] text-muted-foreground">
          <span>{row.invoice_date}</span>
          <span className="font-mono-stat font-semibold text-foreground">
            {fmtUsd(row.amount_usd)}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-1">
          <span className="rounded-sm border border-border bg-muted px-1.5 py-[1px] text-[9.5px] uppercase">
            {row.category}
          </span>
          <button type="button" onClick={onTogglePaid}
                  title={row.paid ? 'Mark unpaid (posts reversing JE)' : 'Mark paid (posts cash JE)'}
                  data-testid={`vinv-toggle-paid-${row.id}`}
                  className="ml-auto inline-flex h-5 items-center rounded-sm border border-border bg-background px-1 text-[9.5px] hover:bg-secondary">
            {row.paid ? <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                       : <Circle className="h-3 w-3 text-muted-foreground" />}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────── Upload Modal ───────────

function UploadModal({ onClose, onUploaded, payables }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    vendor: '', requested_by: '', amount_usd: '',
    invoice_date: new Date().toISOString().slice(0, 10),
    category: 'general', notes: '', payable_id: '',
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!file) { toast.error('Choose a file first.'); return; }
    if (!form.vendor.trim()) { toast.error('Vendor is required.'); return; }
    if (!form.invoice_date) { toast.error('Invoice date is required.'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file',         file);
      fd.append('vendor',       form.vendor.trim());
      fd.append('requested_by', (form.requested_by || '').trim());
      fd.append('amount_usd',   String(parseFloat(form.amount_usd || '0')));
      fd.append('invoice_date', form.invoice_date);
      fd.append('category',     form.category);
      fd.append('notes',        form.notes);
      if (form.payable_id) fd.append('payable_id', form.payable_id);
      await adminApi.vendorInvoiceUpload(fd);
      toast.success('Invoice uploaded. Journal entry posted to Accounts Payable.');
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} title="Upload Vendor Invoice" testid="vinv-upload-modal">
      <div className="grid gap-3 p-4">
        <div>
          <label className="block text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">File</label>
          <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*"
                 onChange={(e) => setFile(e.target.files?.[0] || null)}
                 data-testid="vinv-upload-file"
                 className="mt-1 w-full text-[11.5px]" />
          {file && (
            <div className="mt-1 text-[10.5px] text-muted-foreground">
              {file.name} · {(file.size / 1024).toFixed(1)} KB
            </div>
          )}
        </div>
        <FieldRow label="Vendor" required>
          <input type="text" value={form.vendor}
                 onChange={(e) => set('vendor', e.target.value)}
                 data-testid="vinv-upload-vendor" className="w-full acc-input" />
        </FieldRow>
        <FieldRow label="Requested by">
          <input type="text" value={form.requested_by}
                 onChange={(e) => set('requested_by', e.target.value)}
                 placeholder="Team member who requested the purchase"
                 data-testid="vinv-upload-requested-by" className="w-full acc-input" />
        </FieldRow>
        <div className="grid grid-cols-3 gap-2">
          <FieldRow label="Amount charged (USD)">
            <input type="number" step="0.01" min="0" value={form.amount_usd}
                   onChange={(e) => set('amount_usd', e.target.value)}
                   data-testid="vinv-upload-amount" className="w-full acc-input" />
          </FieldRow>
          <FieldRow label="Invoice date" required>
            <input type="date" value={form.invoice_date}
                   onChange={(e) => set('invoice_date', e.target.value)}
                   data-testid="vinv-upload-date" className="w-full acc-input" />
          </FieldRow>
          <FieldRow label="Category">
            <select value={form.category} onChange={(e) => set('category', e.target.value)}
                    data-testid="vinv-upload-category" className="w-full acc-input">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </FieldRow>
        </div>
        <FieldRow label="Link to open payable (optional)">
          <select value={form.payable_id} onChange={(e) => set('payable_id', e.target.value)}
                  data-testid="vinv-upload-payable" className="w-full acc-input">
            <option value="">— None —</option>
            {payables.map((p) => (
              <option key={p.id} value={p.id}>
                {p.vendor} · {fmtUsd(p.amount_usd)} · due {p.due_date}
              </option>
            ))}
          </select>
        </FieldRow>
        <FieldRow label="Notes">
          <textarea rows={2} value={form.notes}
                    onChange={(e) => set('notes', e.target.value)}
                    data-testid="vinv-upload-notes" className="w-full acc-input" />
        </FieldRow>
      </div>
      <div className="flex justify-end gap-2 border-t border-border bg-muted/30 px-4 py-2">
        <button type="button" onClick={onClose} disabled={busy}
                className="rounded-sm border border-border bg-background px-3 py-1.5 text-[11.5px] hover:bg-secondary">
          Cancel
        </button>
        <button type="button" onClick={submit} disabled={busy}
                data-testid="vinv-upload-submit"
                className="inline-flex items-center gap-1 rounded-sm bg-primary px-4 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
          Upload
        </button>
      </div>
      <style>{`.acc-input { height: 30px; border: 1px solid var(--border, #d0d6dd); border-radius: 3px; padding: 0 8px; font-size: 12px; background: var(--background, #fff); color: inherit; }`}</style>
    </ModalShell>
  );
}

// ────────────────────────────────────────────── Preview Modal ───────────

function PreviewModal({ invoice, payables, onClose, onChanged }) {
  const [draft, setDraft] = useState({
    vendor: invoice.vendor || '',
    requested_by: invoice.requested_by || '',
    amount_usd: invoice.amount_usd ?? '',
    invoice_date: invoice.invoice_date || '',
    category: invoice.category || 'general',
    notes: invoice.notes || '',
    payable_id: invoice.payable_id || '',
  });
  const [busy, setBusy] = useState(false);
  const isImage = (invoice.content_type || '').startsWith('image/');
  const isPdf   = invoice.content_type === 'application/pdf';
  const fileUrl = adminApi.vendorInvoiceFileUrl(invoice.id);

  const save = async () => {
    setBusy(true);
    try {
      const patch = {
        vendor: draft.vendor.trim(),
        requested_by: (draft.requested_by || '').trim(),
        amount_usd: parseFloat(draft.amount_usd || '0'),
        invoice_date: draft.invoice_date,
        category: draft.category,
        notes: draft.notes,
        payable_id: draft.payable_id || null,
      };
      await adminApi.vendorInvoicePatch(invoice.id, patch);
      toast.success('Saved.');
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed.'); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!(await openConfirm({
      title: 'Delete this invoice record?',
      description: 'The file itself remains in Object Storage, but the record is hidden.',
      confirmLabel: 'Delete', tone: 'destructive',
    }))) return;
    setBusy(true);
    try {
      await adminApi.vendorInvoiceDelete(invoice.id);
      toast.success('Deleted.');
      onClose?.();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed.'); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} title={invoice.filename || 'Invoice'}
                testid="vinv-preview-modal" wide>
      <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-5">
        {/* Preview */}
        <div className="md:col-span-3">
          <div className="flex h-[70vh] items-center justify-center overflow-hidden rounded-md border border-border bg-muted/30">
            {isImage ? (
              <img src={fileUrl} alt={invoice.filename}
                   className="max-h-full max-w-full object-contain"
                   data-testid="vinv-preview-image" />
            ) : isPdf ? (
              <embed src={fileUrl} type="application/pdf"
                     className="h-full w-full"
                     data-testid="vinv-preview-pdf" />
            ) : (
              <div className="text-[12px] text-muted-foreground">
                Preview unavailable — click Download to open.
              </div>
            )}
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
            <span>{invoice.content_type} · {(invoice.size_bytes / 1024).toFixed(1)} KB</span>
            <a href={fileUrl} download={invoice.filename}
               data-testid="vinv-preview-download"
               className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary">
              <Download className="h-3 w-3" /> Download
            </a>
          </div>
        </div>

        {/* Metadata */}
        <div className="grid gap-3 md:col-span-2">
          <FieldRow label="Vendor" required>
            <input type="text" value={draft.vendor}
                   onChange={(e) => setDraft((d) => ({ ...d, vendor: e.target.value }))}
                   data-testid="vinv-preview-vendor" className="w-full acc-input" />
          </FieldRow>
          <FieldRow label="Requested by">
            <input type="text" value={draft.requested_by}
                   onChange={(e) => setDraft((d) => ({ ...d, requested_by: e.target.value }))}
                   data-testid="vinv-preview-requested-by" className="w-full acc-input" />
          </FieldRow>
          <div className="grid grid-cols-2 gap-2">
            <FieldRow label="Amount charged (USD)">
              <input type="number" step="0.01" min="0" value={draft.amount_usd}
                     onChange={(e) => setDraft((d) => ({ ...d, amount_usd: e.target.value }))}
                     data-testid="vinv-preview-amount" className="w-full acc-input" />
            </FieldRow>
            <FieldRow label="Invoice date">
              <input type="date" value={draft.invoice_date}
                     onChange={(e) => setDraft((d) => ({ ...d, invoice_date: e.target.value }))}
                     data-testid="vinv-preview-date" className="w-full acc-input" />
            </FieldRow>
          </div>
          <FieldRow label="Category">
            <select value={draft.category}
                    onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
                    data-testid="vinv-preview-category" className="w-full acc-input">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </FieldRow>
          <FieldRow label="Linked payable">
            <select value={draft.payable_id}
                    onChange={(e) => setDraft((d) => ({ ...d, payable_id: e.target.value }))}
                    data-testid="vinv-preview-payable" className="w-full acc-input">
              <option value="">— None —</option>
              {payables.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.vendor} · {fmtUsd(p.amount_usd)} · due {p.due_date}
                </option>
              ))}
              {draft.payable_id && !payables.find((p) => p.id === draft.payable_id) && (
                <option value={draft.payable_id}>(previously linked · {draft.payable_id})</option>
              )}
            </select>
          </FieldRow>
          <FieldRow label="Notes">
            <textarea rows={3} value={draft.notes}
                      onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                      data-testid="vinv-preview-notes" className="w-full acc-input" />
          </FieldRow>
          <div className="rounded-md border border-border bg-muted/30 px-2 py-1.5 text-[11px]">
            <div>Uploaded {invoice.uploaded_at?.slice(0, 10)} by {invoice.uploaded_by}</div>
            {invoice.paid && <div className="text-emerald-600">Paid {invoice.paid_at?.slice(0, 10)}</div>}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/30 px-4 py-2">
        <button type="button" onClick={del} disabled={busy}
                data-testid="vinv-preview-delete"
                className="inline-flex items-center gap-1 rounded-sm border border-rose-300 bg-rose-50 px-2.5 py-1.5 text-[11px] text-rose-800 hover:bg-rose-100">
          <Trash2 className="h-3 w-3" /> Delete
        </button>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} disabled={busy}
                  className="rounded-sm border border-border bg-background px-3 py-1.5 text-[11.5px] hover:bg-secondary">
            Close
          </button>
          <button type="button" onClick={save} disabled={busy}
                  data-testid="vinv-preview-save"
                  className="inline-flex items-center gap-1 rounded-sm bg-primary px-4 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Save
          </button>
        </div>
      </div>
      <style>{`.acc-input { height: 30px; border: 1px solid var(--border, #d0d6dd); border-radius: 3px; padding: 0 8px; font-size: 12px; background: var(--background, #fff); color: inherit; }`}</style>
    </ModalShell>
  );
}

// ─────────────────────────────────────────────── ModalShell ───────────────

function ModalShell({ children, onClose, title, testid, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
                     zIndex: 90, overflow: 'auto', padding: '30px 20px' }}
         data-testid={testid}
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={{ maxWidth: wide ? 1200 : 620, margin: '0 auto',
                       background: 'var(--card, #fff)', color: 'inherit',
                       boxShadow: '0 20px 60px rgba(0,0,0,0.3)', borderRadius: 6,
                       overflow: 'hidden' }}>
        <div className="flex items-center justify-between border-b border-border px-4 py-2">
          <div className="text-[12.5px] font-semibold">{title}</div>
          <button type="button" onClick={onClose} data-testid={`${testid}-close`}
                  className="rounded-sm p-1 hover:bg-secondary">
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FieldRow({ label, required, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}{required && <span className="ml-1 text-destructive">*</span>}
      </span>
      {children}
    </label>
  );
}
